import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import { useKeyboardShortcuts, useToast } from '@/hooks/useKeyboardShortcuts.js';
import {
  TURNO_BY_ID,
  TURNO_CREATE,
  TURNO_UPDATE,
} from '@/graphql/rrhh/turnos.gql.js';

const INITIAL_STATE = {
  nombre: '',
  descripcion: '',
  horaEntrada: '',
  horaSalida: '',
  descansoMinutos: '30',
  aplicaFinesDeSemana: false,
  sucursalId: '',
  activo: true,
};

function validate(form) {
  const errors = {};
  if (!form.nombre.trim()) {
    errors.nombre = 'Ingresá un nombre para el turno.';
  }
  if (!form.horaEntrada) {
    errors.horaEntrada = 'Definí la hora de entrada.';
  }
  if (!form.horaSalida) {
    errors.horaSalida = 'Definí la hora de salida.';
  }
  if (form.horaEntrada && form.horaSalida && form.horaEntrada >= form.horaSalida) {
    errors.horaSalida = 'La salida debe ser posterior a la entrada.';
  }
  if (form.descansoMinutos && Number(form.descansoMinutos) < 0) {
    errors.descansoMinutos = 'El descanso no puede ser negativo.';
  }
  return errors;
}

export default function FormTurno() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const toast = useToast();
  const formRef = useRef(null);

  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [{ data, fetching }] = useQuery({
    query: TURNO_BY_ID,
    variables: { id },
    pause: !isEdit,
  });

  const [, createTurno] = useMutation(TURNO_CREATE);
  const [, updateTurno] = useMutation(TURNO_UPDATE);

  useEffect(() => {
    if (data?.turno) {
      setForm({
        nombre: data.turno.nombre || '',
        descripcion: data.turno.descripcion || '',
        horaEntrada: data.turno.horaEntrada || '',
        horaSalida: data.turno.horaSalida || '',
        descansoMinutos: data.turno.descansoMinutos?.toString() || '30',
        aplicaFinesDeSemana: Boolean(data.turno.aplicaFinesDeSemana),
        sucursalId: data.turno.sucursalId?.toString() || '',
        activo: Boolean(data.turno.activo),
      });
    }
  }, [data]);

  useKeyboardShortcuts(() => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  });

  const pageTitle = useMemo(() => (isEdit ? 'Editar turno' : 'Nuevo turno'), [isEdit]);

  const breadcrumbs = useMemo(() => [
    { label: 'RRHH', href: '/rrhh' },
    { label: 'Turnos', href: '/rrhh/turnos' },
    { label: isEdit ? 'Editar' : 'Nuevo' },
  ], [isEdit]);

  const handleChange = (field) => (event) => {
    const value = field === 'aplicaFinesDeSemana' || field === 'activo'
      ? event.target.checked
      : event.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const validation = validate(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      toast.warning('Revisá los campos marcados antes de guardar.');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion || null,
      horaEntrada: form.horaEntrada,
      horaSalida: form.horaSalida,
      descansoMinutos: Number(form.descansoMinutos) || 0,
      aplicaFinesDeSemana: Boolean(form.aplicaFinesDeSemana),
      sucursalId: form.sucursalId ? Number(form.sucursalId) : null,
      activo: Boolean(form.activo),
    };

    const result = isEdit
      ? await updateTurno({ id, input: payload })
      : await createTurno({ input: payload });

    setIsSubmitting(false);

    if (result.error) {
      toast.error(result.error?.message || 'No pudimos guardar el turno.');
      return;
    }

    toast.success(isEdit ? 'Actualizamos el turno.' : 'Creamos el turno.');
    navigate('/rrhh/turnos');
  };

  return (
    <div className="w-full">
      <PageHeader
        title={pageTitle}
        description="Definí jornadas estándar y configurá descansos."
        breadcrumbs={breadcrumbs}
      />

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="mx-auto grid w-full max-w-[1400px] grid-cols-12 gap-6"
      >
        <section className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.95)] p-6 shadow-[0_8px_30px_rgba(var(--shadow),0.15)]">
          <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Datos del turno</h2>
          <div className="mt-4 grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <Field label="Nombre" htmlFor="nombre" required error={errors.nombre}>
                <input
                  id="nombre"
                  value={form.nombre}
                  onChange={handleChange('nombre')}
                  placeholder="Turno Mañana"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field label="Sucursal" htmlFor="sucursalId">
                <input
                  id="sucursalId"
                  value={form.sucursalId}
                  onChange={handleChange('sucursalId')}
                  placeholder="ID sucursal"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12">
              <Field label="Descripción" htmlFor="descripcion">
                <textarea
                  id="descripcion"
                  value={form.descripcion}
                  onChange={handleChange('descripcion')}
                  rows={3}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="Hora de entrada" htmlFor="horaEntrada" required error={errors.horaEntrada}>
                <input
                  id="horaEntrada"
                  type="time"
                  value={form.horaEntrada}
                  onChange={handleChange('horaEntrada')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="Hora de salida" htmlFor="horaSalida" required error={errors.horaSalida}>
                <input
                  id="horaSalida"
                  type="time"
                  value={form.horaSalida}
                  onChange={handleChange('horaSalida')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-4">
              <Field label="Descanso (minutos)" htmlFor="descansoMinutos" error={errors.descansoMinutos}>
                <input
                  id="descansoMinutos"
                  type="number"
                  min="0"
                  value={form.descansoMinutos}
                  onChange={handleChange('descansoMinutos')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-4">
              <Field label="Aplica fines de semana" htmlFor="aplicaFinesDeSemana">
                <div className="flex items-center gap-3">
                  <input
                    id="aplicaFinesDeSemana"
                    type="checkbox"
                    checked={form.aplicaFinesDeSemana}
                    onChange={handleChange('aplicaFinesDeSemana')}
                    className="h-5 w-5 rounded border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.8)] text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  />
                  <span className="text-sm text-[rgb(var(--fg))]">Incluye sábados y domingos</span>
                </div>
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-4">
              <Field label="Estado" htmlFor="activo">
                <div className="flex items-center gap-3">
                  <input
                    id="activo"
                    type="checkbox"
                    checked={form.activo}
                    onChange={handleChange('activo')}
                    className="h-5 w-5 rounded border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.8)] text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  />
                  <span className="text-sm text-[rgb(var(--fg))]">Turno activo</span>
                </div>
              </Field>
            </div>
          </div>
          {fetching ? (
            <p className="mt-4 text-sm text-[rgba(var(--muted),0.9)]">Cargando datos del turno...</p>
          ) : null}
        </section>
      </form>

      <StickyActions
        onCancel={() => navigate('/rrhh/turnos')}
        onSubmit={() => formRef.current?.requestSubmit()}
        isSubmitting={isSubmitting}
        primaryLabel={isEdit ? 'Guardar cambios' : 'Crear turno'}
      />
    </div>
  );
}
